import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Text,
  Button,
} from 'grommet';
import { Add, Trash } from 'grommet-icons';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  SubmissionLoader,
  AppButton,
  RadioButtonSlider,
} from '@Components/Control';
import {
  ProductFormSingleTextInput,
  ProductFormDropdownSelect,
  ProductFormSectionWrapper,
  ProductFormFieldContainer,
} from '@Components/Partial/Product/NewSearch';
import {
  StyledUploadContainer,
  StyledUploadInput,
} from '@Components/Partial/Product/NewSearch/StyledProductFormComponent.jsx';
import { ManualClaimCsvFieldGuidePartial } from '@Components/Partial/Product/ContentId';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { initiateCreateManualClaimRequest } from '@Actions';
import { MANUAL_CLAIM_SUBMISSION_TYPE } from '@Components/Partial/Product/ContentId/ManualClaims/manualClaimConstants';

/* eslint-disable camelcase -- POST body matches API snake_case contract */
const EMPTY_ITEM = () => ({
  asset_id: '',
  infringing_url: '',
  original_link: '',
  claim_action_type: 'monetize',
  match_type: '',
  match_timeframe: '',
  channel_link: '',
});

const MAX_PASTE_ROWS = 5;
const MAX_CSV_ROWS = 250;
const REQUIRED_CSV_HEADERS = [
  'Asset ID',
  'Infringing URL',
  'Original Link',
  'Claim Type',
];
const CLAIM_TYPE_OPTIONS = ['monetize', 'takedown'];
const MATCH_TYPE_OPTIONS = ['audio', 'video', 'audiovisual'];

const MATCH_TIMEFRAME_PLACEHOLDER = 'Full Match or time range(s) separated by pipes, e.g. 01:00-02:00|03:30-04:00';
const YOUTUBE_VIDEO_URL_RE = /^https?:\/\/(www\.)?(youtube\.com\/(watch|shorts|live)|youtu\.be\/)/;
const YOUTUBE_CHANNEL_URL_RE = /^https?:\/\/(www\.)?youtube\.com\/(channel\/|@|c\/|user\/)/;
/** Header row for downloadable template (required + optional columns). */
const MANUAL_CLAIM_CSV_TEMPLATE_HEADERS = [
  ...REQUIRED_CSV_HEADERS,
  'Match Type',
  'Match Timeframe',
  'Channel Link',
];

const ManualClaimSubmitPage = ({
  small,
  location,
  submitLoading,
  createManualClaimRequest,
  paymentPending,
}) => {
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const [mode, setMode] = React.useState('url');
  const [items, setItems] = React.useState([EMPTY_ITEM()]);
  const [csvFile, setCsvFile] = React.useState(null);
  const [csvBase64, setCsvBase64] = React.useState('');
  const [csvError, setCsvError] = React.useState('');
  const fileInputRef = React.useRef(null);
  const [formError, setFormError] = React.useState('');

  const addRow = () => {
    if (items.length >= MAX_PASTE_ROWS) return;
    setItems((prev) => [...prev, EMPTY_ITEM()]);
  };

  const handleDownloadManualClaimTemplate = () => {
    const csvContent = `${MANUAL_CLAIM_CSV_TEMPLATE_HEADERS.join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'manual_claim_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const removeRow = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, key, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  const handleCsv = (e) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected after fixing errors
    if (fileInputRef.current) fileInputRef.current.value = '';
    setCsvFile(file || null);
    setCsvError('');
    setCsvBase64('');
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields || [];
        const missing = REQUIRED_CSV_HEADERS.filter((h) => !headers.includes(h));
        if (missing.length) {
          setCsvError(`Missing required columns: ${missing.join(', ')}`);
          return;
        }
        if (!results.data?.length) {
          setCsvError('CSV must contain at least one data row.');
          return;
        }
        if (results.data.length > MAX_CSV_ROWS) {
          setCsvError(`Maximum ${MAX_CSV_ROWS} rows per submission.`);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const base64 = typeof dataUrl === 'string' ? dataUrl.split(',')[1] : '';
          setCsvBase64(base64 || '');
        };
        reader.readAsDataURL(file);
      },
      error: () => setCsvError('Invalid CSV file.'),
    });
  };

  const submitUrl = async () => {
    setFormError('');
    const cleaned = items
      .map((row) => ({
        asset_id: row.asset_id.trim(),
        infringing_url: row.infringing_url.trim(),
        original_link: row.original_link.trim(),
        /* API + Joi: `claim_action_type` only; `claim_type` is rejected if sent alongside */
        claim_action_type: row.claim_action_type ?? row.claim_type,
        match_type: row.match_type || undefined,
        match_timeframe: row.match_timeframe || undefined,
        channel_link: row.channel_link?.trim() || undefined,
      }))
      .filter((row) => row.asset_id || row.infringing_url || row.original_link);

    if (!cleaned.length) {
      setFormError('Add at least one complete row with Asset ID, Infringing URL, and Original link.');
      return;
    }

    const incomplete = cleaned.some(
      (row) => !row.asset_id || !row.infringing_url || !row.original_link,
    );
    if (incomplete) {
      setFormError('Each row must include Asset ID, Infringing URL, and Original link.');
      return;
    }

    const invalidInfringing = cleaned.find((row) => !YOUTUBE_VIDEO_URL_RE.test(row.infringing_url));
    if (invalidInfringing) {
      setFormError(`Infringing URL must be a YouTube video URL: "${invalidInfringing.infringing_url}"`);
      return;
    }

    const invalidOriginal = cleaned.find((row) => !YOUTUBE_VIDEO_URL_RE.test(row.original_link));
    if (invalidOriginal) {
      setFormError(`Original link must be a YouTube video URL: "${invalidOriginal.original_link}"`);
      return;
    }

    const invalidChannel = cleaned.find(
      (row) => row.channel_link && !YOUTUBE_CHANNEL_URL_RE.test(row.channel_link),
    );
    if (invalidChannel) {
      setFormError(`Channel link must be a YouTube channel URL: "${invalidChannel.channel_link}"`);
      return;
    }

    if (cleaned.length > MAX_PASTE_ROWS) {
      setFormError(`Paste mode allows at most ${MAX_PASTE_ROWS} claims per request.`);
      return;
    }

    const res = await createManualClaimRequest({
      submission_type: MANUAL_CLAIM_SUBMISSION_TYPE.URL_INPUT,
      items: cleaned,
    });
    if (res) {
      navigate(paths.productAllClaimsList, { state: { view: 'manual' } });
    }
  };

  const submitCsv = async () => {
    setFormError('');
    if (!csvFile || !csvBase64) {
      setFormError('Select a valid CSV file.');
      return;
    }
    const res = await createManualClaimRequest({
      submission_type: MANUAL_CLAIM_SUBMISSION_TYPE.CSV_UPLOAD,
      csv_data: csvBase64,
      csv_filename: csvFile.name,
    });
    if (res) {
      navigate(paths.productAllClaimsList, { state: { view: 'manual' } });
    }
  };

  const onSubmit = async () => {
    if (paymentPending) {
      return;
    }
    if (mode === 'url') {
      await submitUrl();
      return;
    }
    await submitCsv();
  };

  const padding = small ? '1.5rem' : { horizontal: '1.5rem', bottom: '2rem', top: '1rem' };

  /** Equal-width columns on desktop; stacks on small. */
  const col = (child) => (
    <Box
      flex={small ? undefined : { grow: 1, shrink: 1 }}
      basis={small ? undefined : '0'}
      minWidth={small ? undefined : '0'}
      width={small ? '100%' : undefined}
    >
      {child}
    </Box>
  );

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Submit manual claim URLs',
        subTitle: 'Content ID',
        bannerChildrenPosition: 'bottom',
        bannerChildren: (
          <Box direction="row" justify="start" fill="horizontal">
            <RadioButtonSlider
              radioOptions={[
                { label: 'Individual Claims', value: 'url' },
                { label: 'Bulk Claims', value: 'csv' },
              ]}
              componentBg={page.altBg}
              textColor={page.primaryText}
              activeButtonBg={isDarkMode ? page.highlight : 'white'}
              updateParent={setMode}
              value={mode}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.primaryText}
          loaderColor={page.highlight}
          message="Submitting..."
          contentWidth="20rem"
        />
      )}
      <Box flex background={usePageBg} pad={padding} gap="1rem" width="100%">
        <Box fill="horizontal" gap="1rem">
          <Text weight={600} size="1.1rem" color={page.primaryText}>
            Manual claims batch
          </Text>

          {mode === 'url' && (
          <Box gap="medium" width="100%">
            {items.map((row, idx) => (
              <Box
                key={idx} // eslint-disable-line react/no-array-index-key -- ordered form rows
                round="var(--app-radius)"
                pad="1rem"
                background={page.cardBg}
                gap="medium"
                width="100%"
              >
                <Box direction="row" justify="between" align="center">
                  <Text weight={600} size="1rem" color={page.primaryText}>
                    Claim
                    {' '}
                    {idx + 1}
                  </Text>
                  {items.length > 1 && (
                    <Button
                      plain
                      icon={<Trash color={page.highlight} />}
                      onClick={() => removeRow(idx)}
                    />
                  )}
                </Box>
                <Box direction={small ? 'column' : 'row'} gap="medium" align="stretch">
                  {col((
                    <Box gap="0.35rem">
                      <Text size="1rem" color={page.primaryText} weight={600}>Asset ID</Text>
                      <ProductFormSingleTextInput
                        focusHighlight={page.focusHighlight}
                        small={small}
                        value={row.asset_id}
                        handleFormValues={(val) => updateRow(idx, 'asset_id', val)}
                        placeholder="YouTube asset ID"
                      />
                    </Box>
                  ))}
                  {col((
                    <Box gap="0.35rem">
                      <Text size="1rem" color={page.primaryText} weight={600}>Infringing URL</Text>
                      <ProductFormSingleTextInput
                        focusHighlight={page.focusHighlight}
                        small={small}
                        value={row.infringing_url}
                        handleFormValues={(val) => updateRow(idx, 'infringing_url', val)}
                        placeholder="https://..."
                      />
                    </Box>
                  ))}
                  {col((
                    <Box gap="0.35rem">
                      <Text size="1rem" color={page.primaryText} weight={600}>Original link</Text>
                      <ProductFormSingleTextInput
                        focusHighlight={page.focusHighlight}
                        small={small}
                        value={row.original_link}
                        handleFormValues={(val) => updateRow(idx, 'original_link', val)}
                        placeholder="https://..."
                      />
                    </Box>
                  ))}
                </Box>
                <Box direction={small ? 'column' : 'row'} gap="medium" align="stretch">
                  {col((
                    <Box gap="0.35rem">
                      <Text size="1rem" color={page.primaryText} weight={600}>Claim type</Text>
                      <ProductFormDropdownSelect
                        simpleOptions={CLAIM_TYPE_OPTIONS}
                        selectable
                        value={row.claim_action_type}
                        handleFormValues={(val) => updateRow(idx, 'claim_action_type', val)}
                      />
                    </Box>
                  ))}
                  {col((
                    <Box gap="0.35rem">
                      <Text size="1rem" color={page.primaryText} weight={600}>Match type (optional)</Text>
                      <ProductFormDropdownSelect
                        simpleOptions={MATCH_TYPE_OPTIONS}
                        selectable
                        value={row.match_type}
                        handleFormValues={(val) => updateRow(idx, 'match_type', val)}
                      />
                    </Box>
                  ))}
                  {col((
                    <Box gap="0.35rem">
                      <Text size="1rem" color={page.primaryText} weight={600}>Channel link (optional)</Text>
                      <ProductFormSingleTextInput
                        focusHighlight={page.focusHighlight}
                        small={small}
                        value={row.channel_link}
                        handleFormValues={(val) => updateRow(idx, 'channel_link', val)}
                        placeholder="https://..."
                      />
                    </Box>
                  ))}
                </Box>
                <Box gap="0.35rem" width="100%">
                  <Text size="1rem" color={page.primaryText} weight={600}>
                    Match timeframe (optional)
                  </Text>
                  <ProductFormSingleTextInput
                    focusHighlight={page.focusHighlight}
                    small={small}
                    value={row.match_timeframe}
                    handleFormValues={(val) => updateRow(idx, 'match_timeframe', val)}
                    placeholder={MATCH_TIMEFRAME_PLACEHOLDER}
                  />
                </Box>
              </Box>
            ))}
            <Button
              plain
              icon={<Add color={page.highlight} />}
              label="Add more"
              onClick={addRow}
              disabled={items.length >= MAX_PASTE_ROWS}
            />
            <Text size="0.85rem" color={page.hintText}>
              Manual input:
              {' '}
              {items.length}
              {' '}
              /
              {' '}
              {MAX_PASTE_ROWS}
              {' '}
              rows
              {' '}
              ·
              {' '}
              CSV upload supports up to
              {' '}
              {MAX_CSV_ROWS}
              {' '}
              rows
            </Text>
          </Box>
          )}

          {mode === 'csv' && (
          <Box
            flex
            direction={small ? 'column' : 'row'}
            justify={small ? 'start' : 'evenly'}
            gap="2rem"
            width="100%"
          >
            <ProductFormSectionWrapper
              small={small}
              sectionBg={page.altBg}
              sectionTitle="Upload CSV"
              titleColor={page.primaryText}
              boxStyle={!small ? { flex: 1, minWidth: 0 } : {}}
            >
              <ProductFormFieldContainer
                small={small}
                primaryText={page.primaryText}
                hintText={page.hintText}
                childGap="0.75rem"
              >
                <Text size="0.9rem" color={page.primaryText}>
                  Required columns must match the field guide.
                </Text>
                <Text size="0.85rem" color={page.hintText}>
                  CSV upload supports up to
                  {' '}
                  {MAX_CSV_ROWS}
                  {' '}
                  rows per file.
                </Text>
                <StyledUploadContainer highlight={page.focusHighlight}>
                  <Box fill gap="1rem" align="center" direction="row">
                    <Text size="0.875rem" color={page.primaryText}>
                      {csvFile ? csvFile.name : 'No CSV selected'}
                    </Text>
                    <AppButton
                      level="xsmall"
                      label="Choose CSV..."
                      width="fit-content"
                      color={page.altHighlight}
                      disabled={paymentPending || submitLoading}
                      style={{ whiteSpace: 'nowrap', paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                    />
                  </Box>
                  <StyledUploadInput
                    ref={fileInputRef}
                    disabled={paymentPending || submitLoading}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCsv}
                    highlight={page.focusHighlight}
                  />
                </StyledUploadContainer>
                {csvError && <Text color={page.statusError}>{csvError}</Text>}
                {csvFile && !csvError && (
                  <Text color={page.statusSuccess} size="0.85rem">
                    CSV looks valid and is ready to submit.
                  </Text>
                )}

                <Box
                  direction={small ? 'column' : 'row'}
                  gap="small"
                  align={small ? 'stretch' : 'center'}
                  wrap
                  pad={{ top: '0.5rem' }}
                >
                  <AppButton
                    overrideHover
                    level="listRequestNew"
                    label="Submit request"
                    bgColor={button.primaryBg}
                    color={button.primaryText}
                    fontWeight={600}
                    onClick={onSubmit}
                    disabled={paymentPending || submitLoading}
                  />
                  <AppButton
                    overrideHover
                    level="listRequestNew"
                    bgColor={isDarkMode ? page.inputBg : page.cardBg}
                    color={page.primaryText}
                    fontWeight={600}
                    label="Back to claims"
                    onClick={() => navigate(paths.productAllClaimsList, { state: { view: 'manual' } })}
                  />
                </Box>
              </ProductFormFieldContainer>
            </ProductFormSectionWrapper>
            <ManualClaimCsvFieldGuidePartial
              small={small}
              page={page}
              button={button}
              onDownloadTemplate={handleDownloadManualClaimTemplate}
            />
          </Box>
          )}

          {formError && <Text color={page.statusError}>{formError}</Text>}

          {mode === 'url' && (
          <Box direction={small ? 'column' : 'row'} gap="small" align={small ? 'stretch' : 'center'} wrap>
            <AppButton
              overrideHover
              level="listRequestNew"
              label="Submit request"
              bgColor={button.primaryBg}
              color={button.primaryText}
              fontWeight={600}
              onClick={onSubmit}
              disabled={paymentPending || submitLoading}
            />
            <AppButton
              overrideHover
              level="listRequestNew"
              bgColor={isDarkMode ? page.inputBg : page.cardBg}
              color={page.primaryText}
              fontWeight={600}
              label="Back to claims"
              onClick={() => navigate(paths.productAllClaimsList, { state: { view: 'manual' } })}
            />
          </Box>
          )}
        </Box>
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      createManualClaimRequest: initiateCreateManualClaimRequest,
    },
    dispatch,
  );
}

ManualClaimSubmitPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  createManualClaimRequest: PropTypes.func.isRequired,
  paymentPending: PropTypes.bool.isRequired,
};

/* eslint-enable camelcase */

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ManualClaimSubmitPage));
